import { createClient } from "@sanity/client";
import imageUrlBuilder from "@sanity/image-url";

const PROJECT_ID = "en0utxoh";
const DATASET = "production";
const API_VERSION = "2026-04-01";

/** Check if preview mode is active via URL param (?preview=true) */
export function isPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("preview") === "true";
}

/** Standard CDN client for published content */
export const sanityClient = createClient({
  projectId: PROJECT_ID,
  dataset: DATASET,
  apiVersion: API_VERSION,
  useCdn: true,
});

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * Preview client that routes queries through an edge function.
 * The edge function validates the authenticated user has an admin role
 * before querying Sanity with the API read token.
 */
const previewFetch = async (query: string, params?: Record<string, unknown>) => {
  const { supabase } = await import("@/integrations/supabase/client");
  const { data: { session } } = await supabase.auth.getSession();
  const authToken = session?.access_token;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/sanity-preview`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken || SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ query, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Preview fetch failed: ${res.status}`);
  }
  return res.json();
};

/** Wrapper that matches the sanityClient.fetch() signature for preview mode */
export const previewClient = {
  fetch: previewFetch,
};

/** Returns the appropriate client based on preview mode */
export function getClient(): { fetch: (query: string, params?: Record<string, unknown>) => Promise<any> } {
  return isPreviewMode() ? previewClient : sanityClient;
}

const builder = imageUrlBuilder(sanityClient);

type SanityImageSource = Parameters<ReturnType<typeof imageUrlBuilder>["image"]>[0];

export function urlFor(source: SanityImageSource) {
  return builder.image(source);
}
